(() => {
if (customElements.get('ritual-media-stories')) return;
class RitualMediaStories extends HTMLElement {
  constructor() {
    super();
    this.timer = null;
    this.index = 0;
    this.originalCards = [];
    this.cards = [];
    this.loopStart = 0;
    this.loopEnd = 0;
    this.loopSpan = 0;
    this.loopReady = false;
    this.scrollTimer = null;
    this.resizeObserver = null;
    this.videoObserver = null;
    this.dialogWasPlaying = new Set();
    this.userInteracted = false;
  }

  connectedCallback() {
    this.track = this.querySelector('[data-rms-track]');
    this.dialog = this.querySelector('[data-rms-dialog]');
    this.stage = this.querySelector('[data-rms-stage]');
    if (!this.track) return;
    // Shopify may detach and reinsert the same section while it is reordered.
    // Discard the previous loop's copies before deriving the source cards again.
    this.track.querySelectorAll(':scope > [data-rms-clone]').forEach(card => card.remove());
    this.originalCards = [...this.track.querySelectorAll(':scope > [data-rms-card]')];
    this.loopReady = false;

    this.originalCards.forEach((card, index) => { card.dataset.rmsIndex = String(index); });
    this.setupLoop();
    this.setupVideos();

    this.addEventListener('click', this.onClick);
    this.dialog?.addEventListener('close', this.onDialogClose);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.track.addEventListener('scrollend', this.onScrollEnd);
    this.track.addEventListener('pointerdown', this.onUserInteraction, { passive: true });
    this.track.addEventListener('touchstart', this.onUserInteraction, { passive: true });
    this.track.addEventListener('wheel', this.onUserInteraction, { passive: true });
    this.track.addEventListener('keydown', this.onUserKeydown);

    if (this.dataset.pauseHover === 'true') {
      this.addEventListener('mouseenter', this.pauseForHover);
      this.addEventListener('mouseleave', this.resumeFromHover);
      this.addEventListener('focusin', this.pauseForHover);
      this.addEventListener('focusout', this.resumeFromHover);
    }

    this.resizeObserver = new ResizeObserver(() => this.remeasureLoop());
    this.resizeObserver.observe(this.track);

    if (this.dataset.autoplay === 'true' && !matchMedia('(prefers-reduced-motion: reduce)').matches) this.start();
  }

  disconnectedCallback() {
    this.stop();
    clearTimeout(this.scrollTimer);
    this.removeEventListener('click', this.onClick);
    this.dialog?.removeEventListener('close', this.onDialogClose);
    this.track?.removeEventListener('scroll', this.onScroll);
    this.track?.removeEventListener('scrollend', this.onScrollEnd);
    this.track?.removeEventListener('pointerdown', this.onUserInteraction);
    this.track?.removeEventListener('touchstart', this.onUserInteraction);
    this.track?.removeEventListener('wheel', this.onUserInteraction);
    this.track?.removeEventListener('keydown', this.onUserKeydown);
    this.removeEventListener('mouseenter', this.pauseForHover);
    this.removeEventListener('mouseleave', this.resumeFromHover);
    this.removeEventListener('focusin', this.pauseForHover);
    this.removeEventListener('focusout', this.resumeFromHover);
    this.resizeObserver?.disconnect();
    this.videoObserver?.disconnect();
    this.track?.querySelectorAll(':scope > [data-rms-clone]').forEach(card => card.remove());
    this.cards = this.originalCards;
    this.loopReady = false;
    this.querySelectorAll('video').forEach(video => { try { video.pause(); } catch (_) {} });
    document.documentElement.classList.remove('rms-dialog-open');
  }

  setupLoop() {
    if (this.dataset.loop !== 'true' || this.originalCards.length < 2) {
      this.cards = this.originalCards;
      return;
    }

    const before = document.createDocumentFragment();
    const after = document.createDocumentFragment();
    this.originalCards.forEach(card => {
      const a = card.cloneNode(true);
      const b = card.cloneNode(true);
      a.classList.add('is-clone');
      b.classList.add('is-clone');
      a.dataset.rmsClone = 'before';
      b.dataset.rmsClone = 'after';
      a.setAttribute('aria-hidden', 'true');
      b.setAttribute('aria-hidden', 'true');
      a.querySelectorAll('button,a,input').forEach(el => { el.tabIndex = -1; });
      b.querySelectorAll('button,a,input').forEach(el => { el.tabIndex = -1; });
      before.append(a);
      after.append(b);
    });
    this.track.prepend(before);
    this.track.append(after);
    this.cards = [...this.track.querySelectorAll('[data-rms-card]')];
    requestAnimationFrame(() => this.remeasureLoop(true));
  }

  remeasureLoop(initial = false) {
    if (this.dataset.loop !== 'true' || this.originalCards.length < 2) return;
    const firstOriginal = this.originalCards[0];
    const firstAfter = this.track.querySelector('[data-rms-clone="after"]');
    if (!firstOriginal || !firstAfter) return;
    this.loopStart = firstOriginal.offsetLeft;
    this.loopEnd = firstAfter.offsetLeft;
    this.loopSpan = this.loopEnd - this.loopStart;
    if (this.loopSpan <= 0) return;
    if (initial || !this.loopReady) {
      this.track.scrollLeft = this.loopStart;
      this.loopReady = true;
      this.index = 0;
    }
  }

  normalizeLoop() {
    if (!this.loopReady || !this.loopSpan) return false;
    const x = this.track.scrollLeft;
    const tolerance = 3;
    let next = null;
    if (x >= this.loopEnd - tolerance) next = x - this.loopSpan;
    else if (x < this.loopStart - tolerance) next = x + this.loopSpan;
    if (next === null) return false;

    // Normalization must be visually instantaneous. The track uses smooth scrolling
    // for user navigation, so temporarily disable it to avoid a visible rewind.
    const previousBehavior = this.track.style.scrollBehavior;
    this.track.style.scrollBehavior = 'auto';
    this.track.scrollLeft = next;
    // Force the browser to commit the new position before restoring smooth behavior.
    void this.track.offsetWidth;
    this.track.style.scrollBehavior = previousBehavior;
    return true;
  }

  setupVideos() {
    const autoplay = this.dataset.videoAutoplay === 'true';
    const videos = [...this.querySelectorAll('.rms__asset')].filter(media => media instanceof HTMLVideoElement);
    videos.forEach(video => {
      video.muted = true;
      video.defaultMuted = true;
      video.loop = true;
      video.playsInline = true;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      if (autoplay && !this.closest('n-pages')) video.setAttribute('autoplay', '');
      else video.removeAttribute('autoplay');
    });
    if (!autoplay || !videos.length) return;

    this.videoObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        const video = entry.target;
        if (entry.isIntersecting && entry.intersectionRatio >= 0.35 && !this.dialog?.open) {
          video.play().catch(() => {});
        } else {
          try { video.pause(); } catch (_) {}
        }
      });
    }, { root: this.track, threshold: [0, .35, .75] });
    videos.forEach(video => this.videoObserver.observe(video));
    requestAnimationFrame(() => this.playVisibleVideos());
  }

  playVisibleVideos() {
    if (this.dataset.videoAutoplay !== 'true' || this.dialog?.open) return;
    const root = this.track.getBoundingClientRect();
    this.querySelectorAll('.rms__asset').forEach(media => {
      if (!(media instanceof HTMLVideoElement)) return;
      const rect = media.getBoundingClientRect();
      const visible = Math.min(rect.right, root.right) - Math.max(rect.left, root.left);
      const ratio = visible / Math.max(1, rect.width);
      if (ratio >= .35) media.play().catch(() => {});
      else { try { media.pause(); } catch (_) {} }
    });
  }

  onClick = event => {
    const mute = event.target.closest('[data-rms-mute]');
    if (mute && this.contains(mute)) { this.toggleMute(mute); return; }
    const open = event.target.closest('[data-rms-open]');
    if (open && this.contains(open)) { this.openMedia(open); return; }
    if (event.target.closest('[data-rms-close]')) { this.closeMedia(); return; }
    if (event.target.closest('[data-rms-prev]')) { this.onUserInteraction(); this.move(-1, true); return; }
    if (event.target.closest('[data-rms-next]')) { this.onUserInteraction(); this.move(1, true); return; }
    if (event.target === this.dialog || event.target === this.stage) this.closeMedia();
  };


  toggleMute(button) {
    const card = button.closest('[data-rms-card]');
    const video = card?.querySelector('video.rms__asset');
    if (!video) return;
    const nextMuted = !video.muted;
    const index = card?.dataset.rmsIndex;
    this.querySelectorAll(`[data-rms-card][data-rms-index="${index}"] video.rms__asset`).forEach(item => {
      item.muted = nextMuted;
      item.defaultMuted = nextMuted;
      if (nextMuted) item.setAttribute('muted', ''); else item.removeAttribute('muted');
    });
    this.querySelectorAll(`[data-rms-card][data-rms-index="${index}"] [data-rms-mute]`).forEach(control => {
      control.setAttribute('aria-pressed', nextMuted ? 'true' : 'false');
      control.setAttribute('aria-label', nextMuted ? (this.dataset.unmuteLabel || 'Unmute video') : (this.dataset.muteLabel || 'Mute video'));
      control.classList.toggle('is-unmuted', !nextMuted);
    });
    if (!nextMuted) video.play().catch(() => {});
  }

  onScroll = () => {
    // Keep the loop inside the middle copy while momentum scrolling, so the
    // browser never exposes a hard end of the track. The jump is pixel-identical
    // because the neighbouring copies contain the same cards.
    this.normalizeLoop();
    clearTimeout(this.scrollTimer);
    this.scrollTimer = setTimeout(() => {
      this.normalizeLoop();
      this.syncIndex();
      this.playVisibleVideos();
    }, 140);
  };

  onScrollEnd = () => {
    this.normalizeLoop();
    this.syncIndex();
    this.playVisibleVideos();
  };

  syncIndex() {
    if (!this.cards.length) return;
    const left = this.track.scrollLeft;
    let bestCard = null;
    let distance = Infinity;
    this.cards.forEach(card => {
      const d = Math.abs(card.offsetLeft - left);
      if (d < distance) { distance = d; bestCard = card; }
    });
    const parsed = Number(bestCard?.dataset.rmsIndex);
    if (Number.isFinite(parsed)) this.index = parsed;
  }

  onUserInteraction = () => {
    this.userInteracted = true;
    this.stop();
  };

  onUserKeydown = event => {
    if (['ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown'].includes(event.key)) this.onUserInteraction();
  };

  pauseForHover = () => { if (this.dataset.autoplay === 'true') this.stop(); };
  resumeFromHover = () => { if (this.dataset.autoplay === 'true' && !this.userInteracted) this.start(); };

  start() {
    this.stop();
    if (this.userInteracted || this.originalCards.length < 2) return;
    const ms = Math.max(2, Number(this.dataset.interval || 5)) * 1000;
    this.timer = setInterval(() => this.move(1, false), ms);
  }

  stop() {
    if (this.timer) { clearInterval(this.timer); this.timer = null; }
  }

  move(delta, user) {
    if (!this.originalCards.length || !this.track) return;
    this.normalizeLoop();
    this.syncIndex();
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const behavior = reduce ? 'auto' : 'smooth';
    let targetIndex = this.index + delta;
    let target = null;

    if (this.dataset.loop === 'true' && this.loopReady) {
      if (targetIndex >= this.originalCards.length) {
        target = this.track.querySelector('[data-rms-clone="after"][data-rms-index="0"]');
        this.index = 0;
      } else if (targetIndex < 0) {
        target = this.track.querySelector(`[data-rms-clone="before"][data-rms-index="${this.originalCards.length - 1}"]`);
        this.index = this.originalCards.length - 1;
      } else {
        target = this.originalCards[targetIndex];
        this.index = targetIndex;
      }
    } else {
      this.index = (targetIndex + this.originalCards.length) % this.originalCards.length;
      target = this.originalCards[this.index];
    }

    if (target) this.track.scrollTo({ left: target.offsetLeft, behavior });
    clearTimeout(this.scrollTimer);
    this.scrollTimer = setTimeout(() => {
      this.normalizeLoop();
      this.syncIndex();
      this.playVisibleVideos();
    }, reduce ? 40 : 520);
    if (user) this.onUserInteraction();
  }

  openMedia(button) {
    if (this.dataset.viewerDisabled === 'true' || !this.dialog || !this.stage) return;
    this.stop();
    this.dialogWasPlaying.clear();
    this.querySelectorAll('.rms__asset').forEach(media => {
      if (media instanceof HTMLVideoElement) {
        if (!media.paused) this.dialogWasPlaying.add(media);
        try { media.pause(); } catch (_) {}
      }
    });
    this.stage.replaceChildren();
    const asset = button.querySelector('.rms__asset');
    if (!asset) return;
    let node;
    if (asset instanceof HTMLVideoElement) {
      node = asset.cloneNode(true);
      node.controls = true;
      const keepMuted = this.dataset.viewerMuted === 'true';
      node.muted = keepMuted;
      node.defaultMuted = keepMuted;
      if (keepMuted) node.setAttribute('muted', '');
      else node.removeAttribute('muted');
      node.loop = false;
      node.autoplay = true;
      node.playsInline = true;
      node.preload = 'auto';
    } else {
      node = asset.cloneNode(true);
      node.removeAttribute('sizes');
      node.removeAttribute('loading');
      node.src = button.dataset.fullSrc || asset.currentSrc || asset.src;
    }
    this.stage.append(node);
    if (!this.dialog.open) this.dialog.showModal();
    document.documentElement.classList.add('rms-dialog-open');
    if (node instanceof HTMLVideoElement) node.play().catch(() => {});
  }

  closeMedia() {
    if (!this.dialog) return;
    try { this.stage?.querySelector('video')?.pause(); } catch (_) {}
    if (this.dialog.open) this.dialog.close();
    else this.onDialogClose();
  }

  onDialogClose = () => {
    this.stage?.replaceChildren();
    document.documentElement.classList.remove('rms-dialog-open');
    this.playVisibleVideos();
    if (this.dataset.autoplay === 'true' && !this.userInteracted) this.start();
  };
}

if (!customElements.get('ritual-media-stories')) customElements.define('ritual-media-stories', RitualMediaStories);

})();
